import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionWorkloadDetail } from './hooks/useWorkload';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  calculated: 'bg-blue-100 text-blue-600',
  reviewing: 'bg-amber-100 text-amber-600',
  approved: 'bg-emerald-100 text-emerald-600',
  locked: 'bg-slate-900 text-white',
  archived: 'bg-slate-400 text-white',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  calculated: 'Đã tính toán',
  reviewing: 'Đang soát xét',
  approved: 'Đã phê duyệt',
  locked: 'Đã khóa',
  archived: 'Lưu trữ',
};

export default function WorkloadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { workload: item, items, stats, isLoading, updateWorkload } = useConstructionWorkloadDetail(id);

  const [activeTab, setActiveTab] = useState('items');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết khối lượng thi công...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin Workload</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateWorkload({ status_code: newStatus as any });
      toast.success(`Đã cập nhật trạng thái Workload sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const formatCurrency = (val: number) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(val);

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'items', label: 'Chi tiết hạng mục' },
    { value: 'resources', label: 'Nhân công' },
    { value: 'materials', label: 'Vật tư dự kiến' },
    { value: 'equipment', label: 'Thiết bị' },
    { value: 'costs', label: 'Dự toán chi phí' },
  ];

  return (
    <PageDetailLayout
      title={item.name}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{item.workload_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-building-line" />
            {item.project?.name}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Khối lượng thi công', path: '/construction/workload' },
        { label: item.workload_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'reviewing' && (
            <AuthButton module="construction" action="approve" variant="success" icon="ri-check-line" onClick={() => handleStatusChange('approved')}>
              Phê duyệt Workload
            </AuthButton>
          )}
          {item.status_code === 'approved' && (
            <AuthButton module="construction" action="lock" variant="primary" icon="ri-lock-line" onClick={() => handleStatusChange('locked')}>
              Khóa dữ liệu
            </AuthButton>
          )}
          <Button variant="outline" icon="ri-refresh-line">Tính toán lại</Button>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Tổng nhân công" value={stats?.totalManHours.toLocaleString() || '0'} icon="ri-user-settings-line" variant="primary" suffix=" h" />
                <KPICard title="Giờ thiết bị" value={stats?.totalEquipmentHours.toLocaleString() || '0'} icon="ri-tools-line" variant="info" suffix=" h" />
                <KPICard title="Độ khó trung bình" value={stats?.avgDifficulty.toFixed(2) || '1.0'} icon="ri-speed-line" variant="warning" suffix=" / 2.0" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-calculator-line text-os-primary" />
                  Kết quả phân tích định mức
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Tổng hạng mục</p>
                    <p className="text-lg font-bold text-slate-900">{items.length}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Vật tư thi công</p>
                    <p className="text-lg font-bold text-os-primary">{formatCurrency(stats?.materialCost || 0)}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Tổ đội dự kiến</p>
                    <p className="text-lg font-bold text-slate-900">{item.estimated_team_count}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Loại chu kỳ</p>
                    <Badge variant="neutral" className="bg-slate-900 text-white font-bold text-[9px] ">{item.period_type}</Badge>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-information-line text-slate-400" />
                  Mô tả & Định vị
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Khu vực (Zone)</span>
                    <span className="text-sm font-bold text-os-primary underline">{item.zone?.name || 'Toàn dự án'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Giai đoạn (Phase)</span>
                    <span className="text-sm font-bold text-slate-700">{item.phase?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Hợp đồng liên quan</span>
                    <span className="text-sm font-bold text-slate-700">CONT-00129</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Người khởi tạo</span>
                    <span className="text-sm font-bold text-slate-700">Project Manager</span>
                  </div>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-shield-check-line" />
                  Trạng thái Pháp lý
                </h4>
                <div className="space-y-4">
                  <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Căn cứ tính toán</p>
                    <p className="text-xs font-bold text-emerald-400 italic">BOQ v1.2 (Approved)</p>
                  </div>
                  <div className="p-3 bg-slate-800 rounded-2xl border border-slate-700">
                    <p className="text-[10px] text-slate-400 font-bold  mb-1">Hiệu lực sử dụng</p>
                    <p className={`text-xs font-bold ${item.status_code === 'locked' ? 'text-os-primary' : 'text-amber-400'} italic`}>
                      {item.status_code === 'locked' ? 'Đã khóa - Sẵn sàng lập Plan' : 'Chờ phê duyệt dữ liệu'}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-speed-line text-slate-400" />
                  Hệ số ảnh hưởng
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500">Địa hình khó khăn:</span>
                    <Badge variant="neutral" className="bg-amber-50 text-amber-600 font-bold">x 1.2</Badge>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500">Site access hạn chế:</span>
                    <Badge variant="neutral" className="bg-red-50 text-red-600 font-bold">x 1.1</Badge>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500">Thời tiết (mùa mưa):</span>
                    <Badge variant="neutral" className="bg-blue-50 text-blue-600 font-bold">x 1.05</Badge>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'items' && (
          <Card className="overflow-hidden border-none shadow-sm">
            <div className="p-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-700">Chi tiết hạng mục Workload ({items.length})</h3>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" icon="ri-filter-3-line">Lọc hạng mục</Button>
                <AuthButton module="construction" action="update" variant="outline" size="sm" icon="ri-edit-line">Điều chỉnh hệ số</AuthButton>
              </div>
            </div>
            <Table
              columns={[
                { key: 'category', title: 'Phân loại' },
                { key: 'name', title: 'Hạng mục thi công' },
                { key: 'quantity', title: 'Khối lượng', align: 'right' },
                { key: 'man_hours', title: 'Nhân công', align: 'right' },
                { key: 'difficulty', title: 'Hệ số khó', align: 'center' },
                { key: 'cost', title: 'Vật tư dự kiến', align: 'right' },
              ]}
              data={items}
              renderCell={(item, key) => {
                switch (key) {
                  case 'category':
                    return <Badge variant="neutral" className="bg-slate-100 text-slate-500 text-[9px]  font-bold">{item.work_category}</Badge>;
                  case 'name':
                    return (
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-900">{item.boq_item?.item_name || '---'}</span>
                        <span className="text-[10px] text-slate-400 line-clamp-1 italic">{item.work_type}</span>
                      </div>
                    );
                  case 'quantity':
                    return (
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-bold text-slate-900">{item.quantity}</span>
                        <span className="text-[10px] text-slate-400 font-bold ">{item.unit}</span>
                      </div>
                    );
                  case 'man_hours':
                    return <span className="text-xs font-bold text-slate-700">{item.estimated_man_hours.toLocaleString()} h</span>;
                  case 'difficulty':
                    return <Badge variant="neutral" className="bg-amber-50 text-amber-600 font-bold text-[10px]">x {item.difficulty_factor}</Badge>;
                  case 'cost':
                    return <span className="text-sm font-bold text-os-primary">{formatCurrency(item.estimated_material_cost)}</span>;
                  default:
                    return null;
                }
              }}
            />
          </Card>
        )}

        {(activeTab === 'resources' || activeTab === 'materials' || activeTab === 'equipment' || activeTab === 'costs') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được phân rã chi tiết từ các định mức (Rules) và nhu cầu thực tế của từng hạng mục thi công."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
